import {
	Alert,
	Anchor,
	Button,
	Group,
	Loader,
	Paper,
	Stack,
	Text,
	TextInput,
	Title,
} from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	getAdminUserCard,
	guestUserKeys,
} from "@/features/users/api/admin-users";
import { cardPreviewErrorHintKey } from "@/features/users/lib/card-preview-error";
import {
	cardPreviewBase,
	cardPreviewUrl,
	openCardPreviewWindow,
	resolvePreviewSrc,
} from "@/features/users/lib/card-preview-url";

type CardPreviewPanelProps = {
	userId: string;
};

export function CardPreviewPanel({ userId }: CardPreviewPanelProps) {
	const { t } = useTranslation();
	const cardQuery = useQuery({
		queryKey: guestUserKeys.card(userId),
		queryFn: () => getAdminUserCard(userId),
		enabled: Boolean(userId),
		retry: false,
	});

	const remoteUrl = cardQuery.data?.cardUrl ?? "";
	const suggested = cardQuery.data
		? cardPreviewUrl(cardQuery.data.cardId, cardQuery.data.cardUrl)
		: "";

	const [urlField, setUrlField] = useState("");
	const [iframeSrc, setIframeSrc] = useState("");

	useEffect(() => {
		if (!suggested) {
			return;
		}
		setUrlField(suggested);
		setIframeSrc(suggested);
	}, [suggested]);

	const loadFrame = () => {
		setIframeSrc(resolvePreviewSrc(urlField, remoteUrl || suggested));
	};

	const tillOrigin = cardPreviewBase();
	const previewHref = iframeSrc || suggested;

	return (
		<Paper p="md" radius="lg" withBorder>
			<Stack gap="md">
				<Title order={4}>{t("users.cardPreview")}</Title>
				<Text size="sm" c="dimmed">
					{t("users.cardPreviewHint", {
						origin: tillOrigin || t("users.cardPreviewHintRemote"),
					})}
				</Text>

				{cardQuery.isLoading ? (
					<Group justify="center" py="sm">
						<Loader size="sm" />
					</Group>
				) : null}

				{cardQuery.isError ? (
					<Alert color="orange" title={t("users.cardUnavailable")}>
						{t(cardPreviewErrorHintKey(cardQuery.error))}
					</Alert>
				) : null}

				<TextInput
					label={t("users.cardUrl")}
					description={t("users.cardUrlHint")}
					placeholder={
						tillOrigin ? `${tillOrigin}/{code}` : t("users.cardUrlHint")
					}
					value={urlField}
					onChange={(event) => setUrlField(event.currentTarget.value)}
					onKeyDown={(event) => {
						if (event.key === "Enter") {
							event.preventDefault();
							loadFrame();
						}
					}}
				/>
				<Group>
					<Button onClick={loadFrame} disabled={!urlField.trim() && !suggested}>
						{t("users.cardLoad")}
					</Button>
					<Anchor
						href={previewHref || undefined}
						size="sm"
						onClick={(event) => {
							if (!previewHref) {
								return;
							}
							event.preventDefault();
							openCardPreviewWindow(previewHref);
						}}
					>
						{t("users.cardOpenTab")}
					</Anchor>
				</Group>

				{iframeSrc ? (
					<iframe
						title={t("users.cardPreview")}
						src={iframeSrc}
						style={{
							width: "100%",
							maxWidth: 390,
							height: 720,
							border: "1px solid var(--mantine-color-default-border)",
							borderRadius: 16,
							background: "var(--card-device-bg, #fff)",
							display: "block",
						}}
						sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
					/>
				) : null}
			</Stack>
		</Paper>
	);
}
